// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [appLoading, setAppLoading] = useState(false);

  // Register function
  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  };
  const updateWalletBalance = (newBalance) => {
    setUser(prev => ({
      ...prev,
      wallet_balance: newBalance
    }));
  };

  // Fetch dashboard data function - FIXED: No dependencies
  const fetchDashboardData = useCallback(async (force = false) => {
    if (!authService.isAuthenticated()) return null;
    
    // Prevent multiple simultaneous calls
    if (appLoading && !force) return null;
    
    try {
      setAppLoading(true);
      const data = await authService.getDashboardData();
      setDashboardData(data);
      
      // Update user data if dashboard API returned wallet info
      if (data && data.wallet_amt) {
        const currentUser = authService.getCurrentUser();
        const updatedUser = { 
          ...currentUser, 
          wallet_balance: data.wallet_amt,
          mobile_no: data.mobile_no || currentUser?.mobile_no,
          betting_status: data.betting_status,
          account_block_status: data.account_block_status
        };
        authService.updateUserData(updatedUser);
        setUser(updatedUser);
      }
      
      return data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      return null;
    } finally {
      setAppLoading(false);
    }
  }, [appLoading]);

  // Fetch lobby data function
  const fetchLobbyData = useCallback(async () => {
    if (!authService.isAuthenticated()) return null;
    
    try {
      const data = await authService.getLobbyData();
      
      // Update user data if lobby API returned user info
      if (data.user_data) {
        const updatedUser = authService.updateUserData(data.user_data);
        setUser(updatedUser);
      }
      
      return data;
    } catch (error) {
      console.error('Failed to fetch lobby data:', error);
      return null;
    }
  }, []);

  // Initial load effect - FIXED: Only runs once on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const userData = authService.getCurrentUser();
      const token = authService.getToken();
      
      if (userData && token) {
        setUser(userData);
        // Fetch dashboard data on initial load only
        await fetchDashboardData();
      }
      setLoading(false);
    };
    
    initializeAuth();
  }, []); // Empty dependency array - runs only once

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      if (response.success) {
        // Get updated user data after successful login
        const userData = authService.getCurrentUser();
        setUser(userData);
        
        // Fetch dashboard data after successful login
        await fetchDashboardData(true);
        
        return { success: true, data: response };
      } else {
        return { success: false, error: response.msg || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setDashboardData(null);
  };

  const refreshDashboardData = () => {
    return fetchDashboardData(true);
  };

  const refreshLobbyData = () => {
    return fetchLobbyData();
  };
  

  const value = {
    user,
    login,
    register,
    logout,
    dashboardData,
    appLoading,
    refreshDashboardData,
    refreshLobbyData,
    fetchDashboardData,
    fetchLobbyData,
    isAuthenticated: authService.isAuthenticated(),
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};